import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, StatCard, LoadingBlock, EmptyBlock, formatNumber } from '../ui';
import {
  ALERT_TYPES,
  alertDetailText,
  alertSeverityClass,
  alertTypeLabel,
  alertTypePillClass
} from '../components/alertsUi';
import {
  PharmacyAlertsBar,
  PharmacyAlertsDoughnut,
  PharmacyChartCard
} from '../components/PharmacyAlertsCharts';
import {
  buildAlertsSiteBar,
  buildAlertsTypeBar,
  buildAlertsTypePie,
  buildTopMedicationBar
} from '../utils/alertsChartData';

const copy = PHARMACY_COPY.ALERTS;

function severityText(severity) {
  if (severity === 'CRITICAL') return copy.SEVERITY_CRITICAL;
  if (severity === 'WARNING') return copy.SEVERITY_WARNING;
  return '—';
}

export default function PharmacyAlertsPage() {
  const { workSiteId, workSites, reloadNonce, mode } = usePharmacy();
  const siteOnly = mode === 'site';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState(() => (workSiteId ? String(workSiteId) : ''));
  const [siteFilterLockedToWork, setSiteFilterLockedToWork] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 220);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (siteOnly) {
      setSiteFilter(workSiteId ? String(workSiteId) : '');
      setSiteFilterLockedToWork(true);
      return;
    }
    if (!siteFilterLockedToWork) return;
    setSiteFilter(workSiteId ? String(workSiteId) : '');
  }, [workSiteId, siteFilterLockedToWork, siteOnly]);

  const onSiteFilterChange = (value) => {
    if (siteOnly) return;
    setSiteFilter(value);
    setSiteFilterLockedToWork(Boolean(workSiteId) && value === String(workSiteId));
  };

  const effectiveSiteFilter = siteOnly ? (workSiteId ? String(workSiteId) : '') : siteFilter;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (siteOnly && !workSiteId) {
        setData(null);
        return;
      }
      const res = await pharmacyService.getAlerts({
        site_id: effectiveSiteFilter || undefined
      });
      setData(res.success ? res.data : null);
    } finally {
      setLoading(false);
    }
  }, [effectiveSiteFilter, reloadNonce, siteOnly, workSiteId]);

  useEffect(() => {
    load();
  }, [load]);

  const workSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );

  const otherSites = useMemo(
    () => workSites.filter((s) => String(s.id) !== String(workSiteId)),
    [workSites, workSiteId]
  );

  const allAlerts = data?.alerts || [];
  const summary = data?.summary || {
    total: 0,
    out_of_stock: 0,
    low_stock: 0,
    expired: 0,
    expiring: 0
  };

  const filteredAlerts = useMemo(() => {
    let list = allAlerts;
    if (typeFilter) list = list.filter((a) => a.type === typeFilter);
    if (search) {
      list = list.filter((a) => {
        const hay = `${a.medication_name || ''} ${a.site_name || ''} ${a.lot_number || ''} ${a.message || ''}`.toLowerCase();
        return hay.includes(search);
      });
    }
    return list;
  }, [allAlerts, search, typeFilter]);

  const typePieData = useMemo(() => buildAlertsTypePie(summary), [summary]);
  const typeBarData = useMemo(() => buildAlertsTypeBar(summary), [summary]);
  const siteBarData = useMemo(() => buildAlertsSiteBar(allAlerts), [allAlerts]);
  const medBarData = useMemo(() => buildTopMedicationBar(allAlerts), [allAlerts]);

  const toggleTypeFilter = (type) => {
    setTypeFilter((prev) => (prev === type ? '' : type));
  };

  const statCards = [
    { key: '', label: copy.STAT_TOTAL, value: summary.total, tone: 'default', icon: 'alertTriangle' },
    { key: 'OUT_OF_STOCK', label: copy.STAT_OUT, value: summary.out_of_stock, tone: 'alert', icon: 'alertCircle' },
    { key: 'LOW_STOCK', label: copy.STAT_LOW, value: summary.low_stock, tone: 'amber', icon: 'warehouse' },
    { key: 'EXPIRED', label: copy.STAT_EXPIRED, value: summary.expired, tone: 'alert', icon: 'alertTriangle' },
    { key: 'EXPIRING', label: copy.STAT_EXPIRING, value: summary.expiring, tone: 'amber', icon: 'calendar' }
  ];

  return (
    <div className="cp-alerts-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.TITLE}
        description={copy.DESC}
        actions={
          <div className="d-flex flex-wrap gap-2 align-items-center">
            {!siteOnly ? (
              <Form.Select
                size="sm"
                className="cp-dispense-site-filter"
                value={siteFilter}
                onChange={(e) => onSiteFilterChange(e.target.value)}
                aria-label={copy.FILTER_SITE_ARIA}
              >
                <option value="">{copy.FILTER_SITE_ALL}</option>
                {workSiteId ? (
                  <option value={String(workSiteId)}>
                    {copy.FILTER_SITE_WORK}
                    {workSite?.name ? ` — ${workSite.name}` : ''}
                  </option>
                ) : null}
                {otherSites.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            ) : workSite ? (
              <span className="cp-stock-worksite-chip">
                <Icon name="warehouse" size={14} aria-hidden />
                <strong>{workSite.name}</strong>
              </span>
            ) : null}
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={17} aria-hidden />
            </button>
          </div>
        }
      />

      {loading ? (
        <LoadingBlock label={copy.LOADING} />
      ) : !data ? (
        <EmptyBlock label={copy.UNAVAILABLE} />
      ) : (
        <>
          <section className="cp-alerts-stats" aria-label="Synthèse alertes">
            {statCards.map((card) => (
              <button
                key={card.key || 'all'}
                type="button"
                className={`cp-alerts-stat${typeFilter === card.key ? ' is-active' : ''}`}
                onClick={() => toggleTypeFilter(card.key)}
              >
                <StatCard
                  label={card.label}
                  value={formatNumber(card.value)}
                  tone={card.tone}
                  icon={card.icon}
                />
              </button>
            ))}
          </section>

          <section className="cp-alerts-charts">
            <div className="cp-alerts-charts__row">
              <PharmacyChartCard
                title={copy.CHART_TYPE_PIE_TITLE}
                subtitle={copy.CHART_TYPE_PIE_DESC}
                className="cp-alerts-charts__pie"
              >
                <PharmacyAlertsDoughnut data={typePieData} />
              </PharmacyChartCard>

              <PharmacyChartCard
                title={copy.CHART_TYPE_BAR_TITLE}
                subtitle={copy.CHART_TYPE_BAR_DESC}
                className="cp-alerts-charts__type-bar"
              >
                <PharmacyAlertsBar
                  data={typeBarData}
                  xLabel={copy.CHART_AXIS_TYPE}
                  yLabel={copy.CHART_AXIS_COUNT}
                />
              </PharmacyChartCard>
            </div>

            <div className="cp-alerts-charts__row">
              {!siteOnly ? (
                <PharmacyChartCard
                  title={copy.CHART_SITE_BAR_TITLE}
                  subtitle={copy.CHART_SITE_BAR_DESC}
                  className="cp-alerts-charts__site-bar"
                >
                  <PharmacyAlertsBar
                    data={siteBarData}
                    xLabel={copy.CHART_AXIS_SITE}
                    yLabel={copy.CHART_AXIS_COUNT}
                  />
                </PharmacyChartCard>
              ) : null}

              <PharmacyChartCard
                title={copy.CHART_MED_BAR_TITLE}
                subtitle={copy.CHART_MED_BAR_DESC}
                className="cp-alerts-charts__med-bar"
              >
                <PharmacyAlertsBar
                  data={medBarData}
                  xLabel={copy.CHART_AXIS_MED}
                  yLabel={copy.CHART_AXIS_COUNT}
                  options={{
                    scales: {
                      x: {
                        ticks: {
                          maxRotation: 42,
                          minRotation: 0,
                          autoSkip: false,
                          font: { size: 10 }
                        }
                      }
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          title(items) {
                            const item = items?.[0];
                            return item?.label || '';
                          }
                        }
                      }
                    }
                  }}
                />
              </PharmacyChartCard>
            </div>
          </section>

          <div className="cp-card cp-alerts-list-card">
            <div className="cp-catalog-toolbar cp-catalog-toolbar--stack">
              <div className="cp-search-wrap">
                <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
                <input
                  type="search"
                  className="cp-search-wrap__input"
                  placeholder={copy.SEARCH_PLACEHOLDER}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  aria-label={copy.SEARCH_ARIA}
                />
              </div>
              <div className="cp-catalog-toolbar__end cp-catalog-toolbar__end--wrap">
                <div className="cp-filter-pills" role="group" aria-label={copy.FILTER_TYPE_ARIA}>
                  {ALERT_TYPES.map((t) => (
                    <button
                      key={t || 'all'}
                      type="button"
                      className={`cp-filter-pill${typeFilter === t ? ' is-active' : ''}`}
                      onClick={() => setTypeFilter(t)}
                    >
                      {t === '' ? copy.FILTER_ALL : alertTypeLabel(t)}
                    </button>
                  ))}
                </div>
                <div className="cp-catalog-meta">
                  <strong>{formatNumber(filteredAlerts.length)}</strong>
                  <span>{copy.RESULTS_LABEL}</span>
                </div>
              </div>
            </div>

            <div className="cp-card__body cp-card__body--flush">
              {filteredAlerts.length === 0 ? (
                <div className="cp-empty-state">
                  <span className="cp-empty-state__icon" aria-hidden>
                    <Icon name="checkCircle" size={28} />
                  </span>
                  <h3 className="cp-empty-state__title">{copy.EMPTY_TITLE}</h3>
                  <p className="cp-empty-state__text mb-0">{copy.EMPTY_HINT}</p>
                </div>
              ) : (
                <div className="cp-table-wrap">
                  <Table responsive className="cp-table cp-alerts-table mb-0">
                    <thead>
                      <tr>
                        <th>{copy.COL_TYPE}</th>
                        <th>{copy.COL_MED}</th>
                        <th>{copy.COL_SITE}</th>
                        <th>{copy.COL_DETAIL}</th>
                        <th>{copy.COL_SEVERITY}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAlerts.map((a, idx) => (
                        <tr key={`${a.type}-${a.medication_id}-${a.site_id}-${a.lot_id || idx}`}>
                          <td>
                            <span className={`cp-status-pill ${alertTypePillClass(a.type)}`}>
                              {alertTypeLabel(a.type)}
                            </span>
                          </td>
                          <td>
                            <strong>{a.medication_name}</strong>
                          </td>
                          <td>
                            {a.site_name || '—'}
                            {a.is_central ? copy.CENTRAL_SUFFIX : ''}
                          </td>
                          <td className="cp-alerts-table__detail">{alertDetailText(a)}</td>
                          <td>
                            <span className={`cp-status-pill ${alertSeverityClass(a.severity)}`}>
                              {severityText(a.severity)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
